import { doc, getDoc, runTransaction, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { getRolePermissions, normalizeId, normalizePlatformRole } from "../../security/permissions";
import { finalizeFacilityMembership } from "../../finalizeFacilityMembership";

const facilityRoles = ["facility_planner", "facility_admin_viewer"];
const pending = new Map();

export function isFacilityInvitationRole(role) {
  return facilityRoles.includes(normalizePlatformRole(role)) || normalizePlatformRole(role) === "facility_client";
}

// New invitations always use these canonical roles/types. Legacy identities are
// handled by the existing, independently constrained migration path.
export function buildFacilityInvitation({ email, name = "", phone = "", role, tenantId, facilityId }) {
  const cleanEmail = normalizeId(email);
  const tenant = normalizeId(tenantId);
  const facility = normalizeId(facilityId);
  if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) throw new Error("A valid individual email is required.");
  if (!facilityRoles.includes(role)) throw new Error("Select Facility Planner or Facility Pulse Viewer.");
  if (!tenant || tenant === "all" || !facility || facility === "all") throw new Error("Select a specific tenant and facility.");
  return {
    email: cleanEmail, emailLower: cleanEmail, name: name.trim(), phone: phone.trim(),
    role, platformRole: role, accountType: role, primaryRole: role.toUpperCase(), position: role.toUpperCase(),
    tenantId: tenant, facilityId: facility, primaryFacilityId: facility,
    facilityIds: [facility], allowedFacilityIds: [facility], accessScope: "selected_facilities",
    permissions: getRolePermissions(role), permissionVersion: 1, scopeVersion: 1, invitationSchemaVersion: 1,
    active: true, disabled: false, archived: false,
    status: "sent", inviteStatus: "sent", invitationStatus: "sent", registrationStatus: "invited",
    onboardingStatus: "not_applicable", complianceStatus: "not_applicable", workerStatus: "not_applicable",
    readyForShifts: false, documentsSubmitted: false, documentsApproved: false,
    canViewFacilityPulse: true, source: "facility_account_management",
  };
}

export async function createFacilityInvitation(input) {
  const payload = buildFacilityInvitation(input);
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in with an authorized administrator account.");
  const ref = doc(db, "invites", payload.email);
  await runTransaction(db, async (tx) => {
    const facility = await tx.get(doc(db, "facilityProfiles", payload.facilityId));
    const existing = await tx.get(ref);
    if (!facility.exists() || facility.data().tenantId !== payload.tenantId) throw new Error("The facility does not belong to the selected tenant.");
    if (existing.exists()) throw new Error("An invitation already exists for this email. Review the existing account; creating another invitation cannot change its access.");
    tx.set(ref, { ...payload, createdByUid: uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });
  return payload;
}

function assertActive(record, label) {
  if (record.active === false || record.disabled === true || record.archived === true ||
      ["revoked", "disabled", "suspended", "cancelled", "canceled", "expired", "deleted"].includes(record.status)) {
    throw new Error(`${label} is inactive. Contact admin; registration cannot restore revoked access.`);
  }
}

function assertScope(record, expected, label) {
  if (record.tenantId !== expected.tenantId || record.facilityId !== expected.facilityId ||
      record.primaryFacilityId !== expected.facilityId ||
      ![record.facilityIds, record.allowedFacilityIds].every(ids => Array.isArray(ids) && ids.length === 1 && ids[0] === expected.facilityId) ||
      (record.assignedFacilityIds !== undefined && (!Array.isArray(record.assignedFacilityIds) || record.assignedFacilityIds.some(id => id !== expected.facilityId)))) {
    throw new Error(`${label} has missing or conflicting facility scope. Contact admin.`);
  }
}

function validateInvite(invite, user) {
  assertActive(invite, "Invitation");
  const email = normalizeId(user.email);
  if (invite.email !== email || (invite.emailLower && invite.emailLower !== email)) throw new Error("Invitation email does not match the signed-in user.");
  if (!facilityRoles.includes(invite.role) || invite.platformRole !== invite.role || invite.accountType !== invite.role) {
    throw new Error("Facility invitation has a noncanonical role or account type. Ask admin to correct the invitation.");
  }
  if (!invite.tenantId || invite.tenantId === "all" || !invite.facilityId || invite.facilityId === "all") throw new Error("Invitation is missing its tenant or facility.");
  assertScope(invite, invite, "Invitation");
  for (const claimant of [invite.claimedByUid, invite.acceptedBy]) {
    if (claimant && claimant !== user.uid) throw new Error("Invitation already belongs to another account.");
  }
  if (!["sent", "invited", "pending", "registered", "accepted"].includes(invite.status)) throw new Error("Invitation is not available for registration.");
  const expectedPermissions = getRolePermissions(invite.role);
  if (invite.permissions && (Object.keys(invite.permissions).length !== Object.keys(expectedPermissions).length ||
      Object.entries(expectedPermissions).some(([key, value]) => invite.permissions[key] !== value))) {
    throw new Error("Invitation permissions do not match the facility role.");
  }
  return invite;
}

function validateIdentity(record, invite, user, label) {
  assertActive(record, label);
  assertScope(record, invite, label);
  if (record.uid !== user.uid || record.email !== normalizeId(user.email) || record.role !== invite.role || record.platformRole !== invite.role) {
    throw new Error(`${label} conflicts with the invited identity. Contact admin.`);
  }
}

async function completeRegistration(user, inviteId) {
  if (!user?.uid || !user.email || inviteId !== normalizeId(user.email)) throw new Error("Use the exact invited email to register.");
  const userRef = doc(db, "users", user.uid);
  const inviteRef = doc(db, "invites", inviteId);
  const accessRef = doc(db, "accessProfiles", user.uid);
  const registration = await runTransaction(db, async tx => {
    const inviteSnap = await tx.get(inviteRef);
    if (!inviteSnap.exists()) throw new Error("No invitation was found for this email address.");
    const invitation = validateInvite(inviteSnap.data(), user);
    const existing = await tx.get(userRef);
    const access = await tx.get(accessRef);
    if (existing.exists()) {
      validateIdentity(existing.data(), invitation, user, "User profile");
      if (existing.data().inviteId !== inviteId) throw new Error("Existing account requires an administrative access change, not a new registration.");
    }
    if (access.exists()) {
      validateIdentity(access.data(), invitation, user, "Access profile");
      const permissions = getRolePermissions(invitation.role);
      if (Object.keys(access.data().permissions || {}).length !== Object.keys(permissions).length ||
          Object.entries(permissions).some(([key, value]) => access.data().permissions?.[key] !== value)) {
        throw new Error("Existing access permissions require administrative review.");
      }
    }
    if (!existing.exists()) {
      tx.set(userRef, {
        uid: user.uid, email: invitation.email, emailLower: invitation.email,
        name: invitation.name || invitation.email, displayName: invitation.name || invitation.email, phone: invitation.phone || "",
        role: invitation.role, platformRole: invitation.role, accountType: invitation.role,
        primaryRole: invitation.role.toUpperCase(), position: invitation.role.toUpperCase(),
        tenantId: invitation.tenantId, facilityId: invitation.facilityId, primaryFacilityId: invitation.facilityId,
        facilityIds: [invitation.facilityId], allowedFacilityIds: [invitation.facilityId],
        accessScope: "selected_facilities", active: true, disabled: false, archived: false, status: "active",
        inviteId, registrationSchemaVersion: 1, facilityRegistrationComplete: false,
        registrationStatus: "registered", registrationApproved: true,
        onboardingStatus: "not_applicable", onboardingCompleted: true, complianceStatus: "not_applicable",
        workerStatus: "not_applicable", workerOperationalState: "NOT_APPLICABLE",
        canAccessDashboard: true, canAccessOnboarding: false, canViewFacilityPulse: true,
        documentsSubmitted: false, documentsApproved: false, readyForShifts: false,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
    }
    return { invitation, hasAccess: access.exists() };
  });
  const invite = registration.invitation;

  // The rules intentionally require persisted identity before access, and
  // persisted access before roster membership. Resume these stages safely;
  // never declare success or claim an invite after only the identity write.
  const profile = (await getDoc(userRef)).data();
  const hasMembership = invite.role !== "facility_planner" || (registration.hasAccess &&
    (await getDoc(doc(db, "facilityProfiles", invite.facilityId, "planners", user.uid))).exists());
  if (!profile.facilityRegistrationComplete || !registration.hasAccess || !hasMembership ||
      invite.status !== "registered" || invite.claimedByUid !== user.uid) {
    const result = await finalizeFacilityMembership({
      uid: user.uid, email: invite.email, role: invite.role, platformRole: invite.role,
      tenantId: invite.tenantId, facilityId: invite.facilityId, primaryFacilityId: invite.facilityId,
      facilityIds: [invite.facilityId], allowedFacilityIds: [invite.facilityId],
      status: "active", disabled: false, inviteId,
    });
    if (result?.legacyMirror?.failures?.length) throw new Error("Facility membership could not be completed. Sign in to retry or contact admin.");
    await runTransaction(db, async tx => {
      const current = await tx.get(inviteRef);
      const checked = validateInvite(current.data() || {}, user);
      if (checked.role !== invite.role) throw new Error("Invitation changed during registration.");
      assertScope(checked, invite, "Invitation");
      if (checked.status !== "registered" || checked.claimedByUid !== user.uid) {
        tx.update(inviteRef, {
          status: "registered", inviteStatus: "registered", invitationStatus: "registered", registrationStatus: "registered",
          claimedByUid: user.uid, registeredRole: invite.role,
          claimedAt: serverTimestamp(), registeredAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
      }
    });
    await updateDoc(userRef, { facilityRegistrationComplete: true, updatedAt: serverTimestamp() });
  }
  return { applied: true, role: invite.role, tenantId: invite.tenantId, facilityId: invite.facilityId, operationalAccessProvisioned: true, inviteId };
}

export function registerFacilityAccount(user, inviteId = normalizeId(user?.email)) {
  const key = `${user?.uid}:${inviteId}`;
  if (!pending.has(key)) {
    const promise = completeRegistration(user, inviteId).finally(() => pending.delete(key));
    pending.set(key, promise);
  }
  return pending.get(key);
}
